/**
 * Chart Utilities - Konfiguration und Typen fuer die Chart-Library
 *
 * Basiert auf dem shadcn/ui Chart-Ansatz mit Recharts.
 * Alle Charts nutzen CSS-Variablen fuer Farben, passend zum CP Design System.
 */

export interface ChartConfig {
  [key: string]: {
    label: string;
    color: string;
    /** Optionaler Hover-Text, abweichend von `label` (z.B. ohne Prozent-Suffix
     *  der Legende). Fehlt er, fällt der Tooltip auf `label` zurück. */
    tooltipLabel?: string;
    icon?: React.ComponentType;
  };
}

/**
 * Standard-Farbpalette passend zum CP Design System.
 * Nutzt oklch-Farben fuer konsistente Helligkeit.
 */
export const CHART_COLORS = [
  'oklch(60% 0.2 260)',   // brand-primary (blau)
  'oklch(60% 0.18 145)',  // success (gruen)
  'oklch(65% 0.2 45)',    // warm (orange)
  'oklch(55% 0.22 25)',   // destructive (rot)
  'oklch(60% 0.15 310)',  // violet
  'oklch(65% 0.15 195)',  // teal
] as const;

/**
 * Erzeugt CSS-Custom-Properties aus einer ChartConfig.
 * Diese werden als inline-style auf den Container gesetzt.
 */
export function chartConfigToCSS(config: ChartConfig): React.CSSProperties {
  const styles: Record<string, string> = {};
  Object.entries(config).forEach(([key, value]) => {
    styles[`--color-${key}`] = value.color;
  });
  return styles as React.CSSProperties;
}

/**
 * Achsentitel/-einheit fuer eine Cartesian-Achse.
 *
 * Liefert das `label`-Objekt fuer ein Recharts `<XAxis>`/`<YAxis>`. Gibt
 * `undefined` zurueck, wenn keine Einheit gesetzt ist (dann kein Label).
 *
 * @param text   Achsentitel oder Einheit, z.B. "%", "Mio. Zugkilometer", "Jahr"
 * @param axis   'x' (unten zentriert) | 'y' (links, vertikal) | 'yRight' (rechts, vertikal)
 */
export function axisLabel(
  text: string | undefined,
  axis: 'x' | 'y' | 'yRight',
) {
  if (!text) return undefined;
  const style = {
    fontSize: 12,
    fill: 'var(--color-brand-muted, #6b7280)',
    fontWeight: 500,
  } as const;

  if (axis === 'x') {
    return { value: text, position: 'insideBottom' as const, offset: -4, style: { ...style, textAnchor: 'middle' as const } };
  }
  // Y-Achsen: vertikal gedrehter Titel
  return {
    value: text,
    angle: -90,
    position: (axis === 'yRight' ? 'insideRight' : 'insideLeft') as 'insideRight' | 'insideLeft',
    offset: axis === 'yRight' ? 10 : 8,
    style: { ...style, textAnchor: 'middle' as const },
  };
}

/**
 * Chart-Margin, das Platz fuer Achsentitel laesst, sobald eine Achseneinheit
 * gesetzt ist. Ohne Einheiten bleibt es beim kompakten Default-Margin.
 */
export function axisMargin(opts: {
  xUnit?: string;
  yUnit?: string;
  yUnitRight?: string;
}) {
  const { xUnit, yUnit, yUnitRight } = opts;
  return {
    top: 10,
    right: yUnitRight ? 24 : 10,
    left: yUnit ? 16 : 0,
    bottom: xUnit ? 28 : 0,
  };
}

/** Kompaktes Default-Margin ohne Achsentitel. */
export const AXIS_MARGIN = { top: 10, right: 10, left: 0, bottom: 0 } as const;

/**
 * `wrapperStyle` fuer die Recharts-`<Legend>`. Sobald ein X-Achsentitel gesetzt
 * ist, vergroessert sich der obere Abstand, damit Legende und X-Achsen-Titel
 * nicht zusammenlaufen.
 */
export function legendStyle(xUnit?: string): React.CSSProperties {
  return { fontSize: '13px', paddingTop: xUnit ? '22px' : '8px' };
}

/**
 * Bricht ein langes Kategorie-Label in bis zu zwei Zeilen um, damit alle
 * X-Achsen-Ticks sichtbar bleiben (Recharts versteckt sonst überlappende Ticks).
 * Umbruch an Leerzeichen und Schrägstrichen; passt ein Wort nicht, bleibt es
 * ungekürzt in seiner Zeile (lieber leicht überlappend als verschluckt).
 */
export function wrapLabel(label: string, maxChars = 12): string[] {
  const tokens = label.split(/(?<=[ /])/);
  const lines: string[] = [];
  let current = '';
  for (const token of tokens) {
    const candidate = current + token;
    if (current !== '' && candidate.trimEnd().length > maxChars) {
      lines.push(current.trimEnd());
      current = token;
    } else {
      current = candidate;
    }
  }
  if (current.trimEnd() !== '') {
    lines.push(current.trimEnd());
  }
  if (lines.length <= 2) {
    return lines.length > 0 ? lines : [label];
  }
  // Mehr als zwei Zeilen: Rest in die zweite Zeile zusammenführen.
  return [lines[0], lines.slice(1).join(' ')];
}

/**
 * Formatiert Zahlenwerte fuer Tooltips und Labels.
 *
 * Ohne explizite `decimals`-Angabe werden die Werte ungerundet ausgegeben:
 * Ganzzahlen ohne Nachkommastellen, Dezimalwerte mit bis zu 2 Stellen.
 */
export function formatValue(value: number, options?: {
  locale?: string;
  unit?: string;
  decimals?: number;
}): string {
  const { locale = 'de-DE', unit, decimals } = options ?? {};
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 2,
  }).format(value);
  return unit ? `${formatted} ${unit}` : formatted;
}
